
import { filter } from 'lodash';
import Vue from 'vue';
import { transformDate, transformImage, transformLink } from '~/transforms/transformComponents';
import { TransformedLmGrid } from '~/transforms/transformModules';
import { slugify } from '~/utility/helpers';

export default Vue.extend({
  props: {
    data: {
      type: Object as () => TransformedLmGrid['data'],
      required: true,
    },
  },
  data() {
    return {
      queryData: null as any,
      isLoading: true,
      articles: Array(),
      articlesData: Array(),
      filterOnClick: Array(),
      queryDataFromArticles: Array(),
      datasource: null as any,
      totalItems: 0 as number,
      pages: Array(),
      totalPages: 0 as number,
      currentPage: 0,
      availableFilters: { categories: Array(), tags: Array(), subtags: Array() },
      activeFilters: { categories: Array(), tags: Array(), subtags: Array() },
      headerHeight: 0 as number,
      scrollAnimation: false as boolean,
      categoriesData: {} as Record<string, any>,
      tagsData: {} as Record<string, any>,
    };
  },
  async fetch() {
    // Load categories and tags data sources for article filtering and display
    const [categories, tags] = await Promise.all([this.$loadDataSource('categories'), this.$loadDataSource('tags')]);

    this.categoriesData = categories;
    this.tagsData = tags;
  },
  watch: {
    '$route.query': {
      handler() {
        this.setActiveFilters();
      },
      immediate: true,
    },
    activeFilters: {
      async handler() {
        await this.queryStories();
        if (this.scrollAnimation === true || this.$route.query.filter || this.$route.query.page) {
          this.pageTransition();
        }
      },
      deep: true,
    },
  },
  methods: {
    async queryStories(): Promise<boolean> {
      try {
        this.isLoading = true;
        const { market, lang } = this.$i18n.localeProperties;
        const compoundFilter: any = {};

        if (this.activeFilters.tags.length) {
          compoundFilter.tags = {
            any_in_array: this.activeFilters.tags.join(','),
          };
        } else if (this.availableFilters.tags.length && this.activeFilters.tags.length === 0) {
          compoundFilter.tags = {
            any_in_array: this.availableFilters.tags.join(','),
          };
        }

        if (this.activeFilters.subtags.length) {
          compoundFilter.subtags = {
            any_in_array: this.activeFilters.subtags.join(','),
          };
        }

        if (this.activeFilters.categories.length) {
          compoundFilter.category = {
            in: this.activeFilters.categories.join(','),
          };
        }

        //@ts-ignore
        const articlesQueryResult = await this.$store.$storyapi.get(`cdn/stories`, {
          starts_with: `${market}-${lang}/articles`,
          version: process.env.STORYBLOK_CONTENT_VERSION,
          resolve_links: 'url',
          sort_by: 'content.date:desc',
          filter_query: compoundFilter,
          by_uuids: this.data.slides?.join(','),
          per_page: this.data.articlePerPage,
          page: this.currentPage,
        });
        if (articlesQueryResult) {
          this.isLoading = false;
          this.totalItems = articlesQueryResult.headers.total;
          this.articles = this.getTransformedArticles(articlesQueryResult.data.stories);
          this.totalPages = Math.ceil(this.totalItems / Number(this.data.articlePerPage));
        }
        return true;
      } catch (error) {
        return false;
      }
    },

    setFilterQuery(filterProp: string) {
      this.scrollAnimation = true;
      this.hideCards();
      let newFilterQuery: string | (string | null)[] = [];
      if (
        this.$route.query.filter &&
        (typeof this.$route.query.filter == filterProp || this.$route.query.filter.includes(filterProp))
      ) {
        if (typeof this.$route.query.filter === 'string') {
          newFilterQuery = '';
        } else {
          const modifyQuery = [...this.$route.query.filter];
          modifyQuery.splice(this.$route.query.filter.indexOf(filterProp), 1);
          newFilterQuery = modifyQuery;
        }
      } else if (typeof this.$route.query.filter === 'string') {
        newFilterQuery = [this.$route.query.filter, filterProp];
      } else {
        newFilterQuery = [...(this.$route.query.filter || []), filterProp];
      }
      this.$router.push({
        query: { ...this.$route.query, ...{ filter: newFilterQuery, page: '1' } },
      });
    },

    isActiveFilter(filterProp: string) {
      const filters = this.getActiveFilters();
      let filterActive = false;
      Object.entries(filters).forEach(([filterCat, filterProps]: any) => {
        if (filterProps.includes(filterProp)) filterActive = true;
      });
      return filterActive;
    },

    setPage(page: any) {
      if (this.currentPage == page) return;
      this.$router.push({ query: { ...this.$route.query, page: page } });
      this.hideCards();
      this.scrollAnimation = true;
    },

    checkHeadlineMargin() {
      if (this.data.moduleHeader) {
        return this.data.moduleHeader.headline.text?.trim() ? null : 'no-margin-top';
      }
    },

    setActiveFilters() {
      this.activeFilters.categories = this.data.categories?.filter(cat => !!cat) || [];
      this.currentPage = Number(this.$route?.query?.page) || 1;

      if (!this.$route?.query?.filter) {
        this.activeFilters.tags = [];
        if (this.availableFilters.tags.length === 1) {
          this.activeFilters.tags = this.availableFilters.tags;
        }
      } else {
        if (this.availableFilters.tags.length === 1) {
          this.activeFilters.tags = this.availableFilters.tags;
          this.activeFilters.subtags =
            typeof this.$route.query.filter === 'string' ? [this.$route.query.filter] : [...this.$route.query.filter];
        } else {
          this.activeFilters.tags =
            typeof this.$route.query.filter === 'string' ? [this.$route.query.filter] : [...this.$route.query.filter];
        }
      }
    },

    getActiveFilters() {
      return this.activeFilters;
    },

    async createFilters() {
      if (
        (this.data.categories?.length ?? 0) > 0 &&
        (this.data.tags?.length ?? 0) > 0 &&
        (!this.data.subtags || this.data.subtags?.length === 0)
      ) {
        this.availableFilters.tags = this.data.tags?.map(tag => tag) ?? [];
      } else if (
        (this.data.categories?.length ?? 0) > 0 &&
        this.data.tags?.length === 1 &&
        (this.data.subtags?.length ?? 0) > 0
      ) {
        this.activeFilters.tags = this.data.tags.map(tag => tag);
        this.availableFilters.tags = this.data.tags.map(tag => tag);
        this.availableFilters.subtags = this.data.subtags?.map(subtag => subtag) ?? [];
      }
      this.availableFilters.categories = this.data.categories?.map(cat => !!cat) ?? [];
      return true;
    },

    getTransformedArticles(articles: any): any {
      const getI18nCategory = (categoryKey: string) => {
        if (categoryKey) {
          return this.categoriesData[slugify(categoryKey)]?.value || categoryKey;
        }
      };
      const getI18nTags = (tags: any) => {
        if (tags) {
          return tags.map((dataSourceItem: string) => this.tagsData[slugify(dataSourceItem)]?.value || dataSourceItem);
        }
      };

      return articles.map((articleItem: any) => ({
        category: getI18nCategory(articleItem.content.category),
        tags: getI18nTags(articleItem.content.tags),
        image: transformImage(articleItem.content.image, '16to9', undefined, 'md:100vw 2xl:50vw'),
        date: transformDate(articleItem.content.date),
        subtags: getI18nTags(articleItem.content.tags),
        link: transformLink(articleItem),
        text: articleItem.content.stageHeadline.replace(/<br>\s{0,1}/g, ' '),
        isActive: false,
      }));
    },

    pageTransition() {
      if (process.client) {
        const cardsCt = this.$refs.cardsCt as HTMLElement;
        const gridCt = this.$refs.gridCt as HTMLElement;
        cardsCt.classList.remove('lm-grid__hide');
        if (gridCt) {
          window.scrollTo({
            top: gridCt.getBoundingClientRect().top + window.scrollY,
            behavior: 'smooth',
          });
        }
        cardsCt.style.height = '';
      }
    },
    getContainerHeight() {
      const cardsCt = this.$refs.cardsCt as HTMLElement;
      let containerHeight;
      containerHeight = cardsCt.clientHeight;
      cardsCt.style.height = containerHeight + 'px';
    },
    hideCards() {
      const cardsCt = this.$refs.cardsCt as HTMLElement;
      cardsCt.classList.add('lm-grid__hide');
    },
    // show cards
    showCards() {
      const cardsCt = this.$refs.cardsCt as HTMLElement;
      cardsCt.classList.remove('lm-grid__hide');
    },
    nextPage() {
      if (this.currentPage !== this.totalPages) {
        this.setPage(this.currentPage + 1);
      }
    },
    prevPage() {
      if (this.currentPage !== 1) {
        this.setPage(this.currentPage - 1);
      }
    },
  },
  async mounted() {
    await this.createFilters();
    if (!this.$route.query.length) this.setActiveFilters();
  },
});
